import { Spinner } from '../../components/Spinner.js';
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { apiClient } from '../../lib/api-client.js';
import { useAuth } from '../auth/useAuth.js';
import { RankingList } from './RankingList.js';
import { MyRankBanner } from './MyRankBanner.js';

export interface RankingEntry {
  rank: number;
  userId: string;
  nickname: string;
  avatarUrl: string | null;
  checkinRank: string;
  spotMakerRank: string;
  score: number;
}

export interface RankingResult {
  data: RankingEntry[];
  meta: { total: number; page: number; limit: number; totalPages: number };
  myRank: { rank: number; score: number } | null;
  periodLabel: string | null;
}

export type RankingType = 'weekly' | 'monthly' | 'annual' | 'total';
export type RankingCategory = 'checkin' | 'spot';

const TYPE_LABELS: Record<RankingType, string> = {
  weekly: '週間',
  monthly: '月間',
  annual: '年間',
  total: 'トータル',
};

const CATEGORY_LABELS: Record<RankingCategory, string> = {
  checkin: 'チェックイン',
  spot: 'スポット登録',
};

const RANKING_TYPES: RankingType[] = ['weekly', 'monthly', 'annual', 'total'];
const RANKING_CATEGORIES: RankingCategory[] = ['checkin', 'spot'];

export function RankingView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { dbUser } = useAuth();

  const type = (searchParams.get('type') as RankingType) ?? 'weekly';
  const category = (searchParams.get('category') as RankingCategory) ?? 'checkin';

  const [result, setResult] = useState<RankingResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRanking = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.get<RankingResult>(
        `/ranking?type=${type}&category=${category}&limit=100`
      );
      setResult(data);
    } catch {
      setError('ランキングの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [type, category]);

  useEffect(() => {
    void fetchRanking();
  }, [fetchRanking]);

  function setType(t: RankingType) {
    setSearchParams({ type: t, category });
  }

  function setCategory(c: RankingCategory) {
    setSearchParams({ type, category: c });
  }

  const showMyRankBanner = dbUser && result?.myRank && result.myRank.rank > 100;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 pt-4 pb-0">
          <h1 className="text-xl font-bold text-gray-900 mb-3">ランキング</h1>

          {/* 期間タブ */}
          <div className="flex border-b border-gray-200 -mb-px">
            {RANKING_TYPES.map((t) => (
              <button
                key={t}
                onClick={() => setType(t)}
                className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                  type === t
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {TYPE_LABELS[t]}
              </button>
            ))}
          </div>
        </div>

        {/* カテゴリ切り替え */}
        <div className="flex px-4 py-2 gap-2">
          {RANKING_CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`flex-1 py-1.5 rounded-full text-sm font-medium transition-colors text-center ${
                category === c
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {CATEGORY_LABELS[c]}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 py-3">
        {/* 集計期間ラベル */}
        {!loading && result?.periodLabel && (
          <div className="text-xs text-gray-500 text-center mb-2">
            集計期間: {result.periodLabel}
          </div>
        )}

        {showMyRankBanner && result?.myRank && (
          <MyRankBanner
            rank={result.myRank.rank}
            score={result.myRank.score}
            thresholdScore={result.data[result.data.length - 1]?.score ?? null}
            category={category}
          />
        )}

        {loading && (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        )}

        {error && !loading && <div className="text-center py-12 text-red-500">{error}</div>}

        {!loading && !error && result && result.data.length > 0 && (
          <RankingList entries={result.data} myUserId={dbUser?.id ?? null} category={category} />
        )}

        {!loading && !error && result?.data.length === 0 && type !== 'total' && (
          <div className="text-center py-12 text-gray-400">前期間の集計データはまだありません</div>
        )}

        {!loading && !error && result?.data.length === 0 && type === 'total' && (
          <div className="text-center py-12 text-gray-400">まだランキングデータがありません</div>
        )}
      </div>
    </div>
  );
}
